import React, { useState } from 'react';
import {
  Box,
  Collapse,
  IconButton,
  Step,
  StepButton,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useLocation, useNavigate } from 'react-router-dom';
import { WORKFLOW_STEPS, stepIndexFromPath } from '../config/workflowSteps';
import { hasPermission } from '../../subscriptionAdmin/permissions';

export default function WorkflowStepper({ activeStep: activeStepProp }) {
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);
  const activeStep =
    activeStepProp ?? stepIndexFromPath(location.pathname, location.search);

  const visibleSteps = WORKFLOW_STEPS.filter(
    (s) => !s.permission || hasPermission(s.permission)
  );

  const currentStep = visibleSteps.find((s) => s.id === activeStep)
    || visibleSteps.find((s) => s.id === Math.max(0, activeStep));

  const toggleExpanded = () => setExpanded((prev) => !prev);

  return (
    <Box
      sx={{
        mb: 3,
        bgcolor: 'white',
        borderRadius: 2,
        boxShadow: '0 1px 3px rgba(15,23,42,0.08)',
        border: '1px solid rgba(15,23,42,0.06)',
        overflow: 'hidden',
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        onClick={toggleExpanded}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleExpanded();
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 2,
          py: 1.25,
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { bgcolor: 'rgba(15,23,42,0.02)' },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
            Subscription pipeline (steps 0–11)
          </Typography>
          {!expanded && currentStep && (
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a', mt: 0.25 }}>
              Current: {currentStep.title}
            </Typography>
          )}
        </Box>
        <IconButton
          size="small"
          aria-label={expanded ? 'Collapse pipeline' : 'Expand pipeline'}
          aria-expanded={expanded}
          onClick={(e) => {
            e.stopPropagation();
            toggleExpanded();
          }}
          sx={{ color: '#64748b', flexShrink: 0 }}
        >
          {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded} timeout="auto">
        <Box sx={{ px: 2, pb: 2, overflowX: 'auto' }}>
          <Stepper
            activeStep={activeStep < 0 ? 0 : activeStep}
            alternativeLabel={!compact}
            orientation={compact ? 'vertical' : 'horizontal'}
            nonLinear
          >
            {visibleSteps.map((step) => (
              <Step key={step.key} completed={activeStep > step.id}>
                <StepButton onClick={() => navigate(step.path)}>
                  <StepLabel
                    optional={
                      compact ? null : (
                        <Typography variant="caption" sx={{ display: { xs: 'none', lg: 'block' } }}>
                          {step.subtitle}
                        </Typography>
                      )
                    }
                  >
                    {step.title}
                  </StepLabel>
                </StepButton>
              </Step>
            ))}
          </Stepper>
        </Box>
      </Collapse>
    </Box>
  );
}
